/**
 * Family-account picker (#2261): a server-searched dropdown over
 * GET /api/v1/family-groups. Each row shows the family account the way the
 * debtor list does (label, children, outstanding / credit / net), plus its
 * lifecycle and whether it shares a parent with the contract's child.
 */
import { useState } from "react";
import { useLingui } from "@lingui/react";
import type { I18n } from "@lingui/core";
import { Check } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { ServerSearchCombobox } from "@/components/ui/server-search-combobox";
import { useCurrency } from "@/hooks/useCurrency";
import {
  FAMILY_GROUP_PICKER_PAGE_SIZE,
  useFamilyGroupPicker,
} from "@/hooks/useFamilyGroups";
import { formatMoneyCents } from "@/lib/money";
import { cn } from "@/lib/utils";
import { debtorChildrenSuffix } from "@/components/billing/reconciliation/redesign/lib/selectors";
import type { FamilyGroupPickerItem } from "@/types/billing";

const SEARCH_DEBOUNCE_MS = 250;

/** "Family {surname}", the same message the debtor list uses for family rows. */
export function useFamilyAccountLabel(): (familyName: string) => string {
  const { i18n } = useLingui();
  return (familyName: string) =>
    i18n._({
      id: "reconciliation.label.family",
      message: "Family {surname}",
      values: { surname: familyName },
    });
}

function lifecycleBadge(
  lifecycle: FamilyGroupPickerItem["lifecycle"],
  i18n: I18n,
): { label: string; variant: BadgeProps["variant"] } {
  switch (lifecycle) {
    case "current":
      return {
        label: i18n._({
          id: "reconciliation.debtorAccounts.lifecycle.current",
          message: "Current",
        }),
        variant: "success",
      };
    case "left":
      return {
        label: i18n._({
          id: "reconciliation.debtorAccounts.lifecycle.left",
          message: "Left",
        }),
        variant: "secondary",
      };
    default:
      // Also a group whose only contract was deleted: its children list is empty.
      return {
        label: i18n._({
          id: "contracts.familyAccount.lifecycle.none",
          message: "No contract",
        }),
        variant: "warning",
      };
  }
}

function outstandingClass(cents: number): string {
  return cents > 0 ? "font-medium text-destructive" : "text-muted-foreground";
}

function creditClass(cents: number): string {
  return cents > 0 ? "font-medium text-info-on-surface" : "text-muted-foreground";
}

export function netBalanceClass(cents: number): string {
  if (cents > 0) return "text-success-on-surface";
  if (cents < 0) return "text-destructive";
  return "text-muted-foreground";
}

interface FamilyAccountRowProps {
  item: FamilyGroupPickerItem;
  selected?: boolean;
}

/** Row content shared by the dropdown and the suggestion cards. */
export function FamilyAccountRow({ item, selected = false }: FamilyAccountRowProps) {
  const { i18n } = useLingui();
  const { currency } = useCurrency();
  const familyLabel = useFamilyAccountLabel();
  const lifecycle = lifecycleBadge(item.lifecycle, i18n);
  const childrenLine = debtorChildrenSuffix({ children: item.children });

  return (
    <div className="flex w-full min-w-0 items-start gap-2">
      <Check
        className={cn("mt-0.5 h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-medium">
            {familyLabel(item.familyName)}
          </span>
          <Badge variant={lifecycle.variant} className="text-xs">
            {lifecycle.label}
          </Badge>
          {item.sharesParent && (
            <Badge variant="info" className="text-xs">
              {i18n._({
                id: "contracts.familyAccount.badge.sameParents",
                message: "Same parents",
              })}
            </Badge>
          )}
        </div>
        {childrenLine && (
          <div className="truncate text-xs text-muted-foreground">{childrenLine}</div>
        )}
        {item.debtorAccountId ? (
          <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
            <div className="flex gap-1">
              <dt className="text-muted-foreground">
                {i18n._({
                  id: "reconciliation.portfolio.column.outstanding",
                  message: "Outstanding",
                })}
              </dt>
              <dd className={cn("tabular-nums", outstandingClass(item.totalOutstandingCents))}>
                {formatMoneyCents(item.totalOutstandingCents, currency)}
              </dd>
            </div>
            <div className="flex gap-1">
              <dt className="text-muted-foreground">
                {i18n._({
                  id: "reconciliation.portfolio.column.credit",
                  message: "Available credit",
                })}
              </dt>
              <dd className={cn("tabular-nums", creditClass(item.unappliedCreditCents))}>
                {formatMoneyCents(item.unappliedCreditCents, currency)}
              </dd>
            </div>
            <div className="flex gap-1">
              <dt className="text-muted-foreground">
                {i18n._({ id: "reconciliation.portfolio.column.net", message: "Net" })}
              </dt>
              <dd className={cn("tabular-nums", netBalanceClass(item.netBalanceCents))}>
                {formatMoneyCents(item.netBalanceCents, currency)}
              </dd>
            </div>
          </dl>
        ) : (
          // No invoice was finalized for this group yet, so no debtor account
          // exists and all balances are zero by definition.
          <div className="text-xs text-muted-foreground">
            {i18n._({
              id: "contracts.familyAccount.noAccountYet",
              message: "No account yet",
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export interface FamilyAccountPickerProps {
  /** Id of the selected family group. */
  value?: string;
  /**
   * The selected row, resolved by the caller. Under server search the
   * selected group is often not on the current result page.
   */
  selectedItem?: FamilyGroupPickerItem;
  /** Trigger text when `value` is set but no row is resolved (e.g. still loading). */
  selectedFallbackName?: string;
  onSelect: (item: FamilyGroupPickerItem) => void;
  /** Child whose parents mark rows as "Same parents" and sort them first. */
  childId?: string;
  excludeFamilyGroupId?: string;
  triggerId?: string;
  triggerAriaLabel?: string;
  disabled?: boolean;
}

export function FamilyAccountPicker({
  value,
  selectedItem,
  selectedFallbackName,
  onSelect,
  childId,
  excludeFamilyGroupId,
  triggerId,
  triggerAriaLabel,
  disabled = false,
}: FamilyAccountPickerProps) {
  const { i18n } = useLingui();
  const { currency } = useCurrency();
  const familyLabel = useFamilyAccountLabel();
  const [search, setSearch] = useState("");
  const { data, isLoading, isFetching, isError } = useFamilyGroupPicker({
    search,
    childId,
    excludeFamilyGroupId,
    limit: FAMILY_GROUP_PICKER_PAGE_SIZE,
  });
  const items = data?.data ?? [];
  const resolvedSelected =
    selectedItem ?? (value ? items.find((item) => item.id === value) : undefined);

  return (
    <ServerSearchCombobox<FamilyGroupPickerItem>
      value={value}
      selectedItem={resolvedSelected}
      items={items}
      getItemId={(item) => item.id}
      onChange={(_id, item) => {
        // Re-selecting the current row would clear it; a contract always
        // needs a family account here, so a clear keeps the selection.
        if (item) onSelect(item);
      }}
      onDebouncedSearchChange={setSearch}
      debounceMs={SEARCH_DEBOUNCE_MS}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      disabled={disabled}
      triggerId={triggerId}
      triggerAriaLabel={triggerAriaLabel}
      triggerClassName="h-auto py-2"
      contentClassName="min-w-80"
      searchPlaceholder={i18n._({
        id: "contracts.familyAccount.picker.searchPlaceholder",
        message: "Search by family name or child",
      })}
      emptyMessage={i18n._({
        id: "contracts.familyAccount.picker.empty",
        message: "No family accounts found",
      })}
      loadingMessage={i18n._({
        id: "contracts.familyAccount.picker.loading",
        message: "Loading family accounts...",
      })}
      errorMessage={i18n._({
        id: "contracts.familyAccount.picker.error",
        message: "Family accounts could not be loaded",
      })}
      renderItem={(item, isSelected) => (
        <FamilyAccountRow item={item} selected={isSelected} />
      )}
      renderTrigger={(selected) => {
        if (selected) {
          return (
            <span className="flex min-w-0 items-center gap-2 truncate">
              <span className="truncate font-medium">{familyLabel(selected.familyName)}</span>
              <span className={cn("tabular-nums text-xs", netBalanceClass(selected.netBalanceCents))}>
                {formatMoneyCents(selected.netBalanceCents, currency)}
              </span>
            </span>
          );
        }
        if (value && selectedFallbackName) {
          return <span className="truncate font-medium">{familyLabel(selectedFallbackName)}</span>;
        }
        return (
          <span className="text-muted-foreground">
            {i18n._({
              id: "contracts.familyAccount.picker.placeholder",
              message: "Choose another family account...",
            })}
          </span>
        );
      }}
    />
  );
}
